import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LoadingScreen } from "@/components/common/LoadingScreen";

/** Blocks every route below it when no authenticated session exists. */
export const ProtectedRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
};

/** Client-side navigation boundary only; platform APIs enforce the database flag separately. */
export const RequirePlatformAdmin = () => {
  const { user, loading, isPlatformAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: "/app/admin" }} />;
  if (!isPlatformAdmin) return <Navigate to="/app" replace />;
  return <Outlet />;
};

/** Sends authenticated users away from public auth pages. */
export const PublicOnlyRoute = () => {
  const { user, loading, isPlatformAdmin, organizations } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={isPlatformAdmin && !organizations.length ? "/app/admin" : "/app"} replace />;
  return <Outlet />;
};

/**
 * Requires an active organization whose onboarding is completed.
 * Membership itself is verified server-side on every request (X-Organization-Id + membership check).
 */
export const RequireOrganization = () => {
  const { user, loading, organizations, currentOrg } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!organizations.length || !currentOrg) return <Navigate to="/onboarding" replace />;
  if (!currentOrg.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
};
